<script setup lang="ts">
import { computed } from "vue";

import { EDashboardType } from "@/app/data/dashboard/dashboard.types";
import { TCategorizedDataResponseSerie } from "@/components/ui/chart/types/categorized-chart-series.type";
import { useFeatureFlag } from "@/composables/feature-flag/use-feature-flag";
import { EColumnM2Consumption } from "@/data/api/kili/consumption/types/consumption-metrics.type";
import { keyOf, useTypedIntl } from "@/locales/typed-intl";
import { ERouteName } from "@/router/router.type";
import { newSectionManagementFeatureFlag } from "@/services/feature-flags/env-feature-flags";
import { YAxisAdapter } from "@/utils/chart/adapters/axis/y-axis-adapter";
import { ColumnSeriesAdapter, IColumnGradientColors } from "@/utils/chart/adapters/series/column-series-adapter";
import { getWhSeriesHighestPrefixSymbol } from "@/utils/chart/chart-series-utils";
import { IColumnChartSpecs } from "@/utils/chart/column-chart-options-utils";

import DashboardCard from "@/components/data/dashboard/dashboard-card.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import BaseColumnChart from "@/components/ui/chart/base-column-chart.vue";

interface IProps {
  series?: TCategorizedDataResponseSerie<EColumnM2Consumption>[];
  isLoading?: boolean;
  type: EDashboardType;
  hasNoData?: boolean;
}

const props = defineProps<IProps>();

const { t, k, rawT } = useTypedIntl();
const { isEnvFeatureEnabled } = useFeatureFlag();

const columnColorOpts = computed<IColumnGradientColors>(() => {
  return props.type === EDashboardType.MONOSITE
    ? {
        firstStop: "#7FB9FF",
        secondStop: "#CCE0FF",
      }
    : {
        firstStop: "#66E9D3",
        secondStop: "#C8F7EE",
      };
});

const sitesManagementTab = computed<string>(() =>
  t(k.components.layout.sideMenu.sitesManagement({ count: props.type === EDashboardType.MONOSITE ? 1 : 2 })),
);

const sectionConsumptionCostChartSpecs = computed<IColumnChartSpecs<EColumnM2Consumption> | undefined>(() => {
  if (!props.series) return undefined;

  const maybePrefixSymbol = getWhSeriesHighestPrefixSymbol(props.series);

  return {
    seriesAdapter: [
      [
        EColumnM2Consumption.AREA_CONSUMPTION,
        ColumnSeriesAdapter({
          name: t(k.data.dashboard.areaConsumptionChart.legend[props.type]),
          colorGradient: columnColorOpts.value,
          prefixSymbol: maybePrefixSymbol,
          accessibilityDescription: t(k.data.dashboard.areaConsumptionChart.description[props.type]),
        }),
      ],
    ],
    yAxisAdapter: [
      [
        EColumnM2Consumption.AREA_CONSUMPTION,
        YAxisAdapter({
          prefixSymbol: maybePrefixSymbol,
        }),
      ],
    ],
    plotOpts: {
      prefixSymbol: maybePrefixSymbol,
    },
    accessibilityDescription: t(
      k.data.dashboard.areaConsumptionChart.tooltip[props.type]({ sitesManagementTab: sitesManagementTab.value }),
    ),
  };
});
</script>

<template>
  <DashboardCard
    :infos="true"
    :title="rawT(`data.dashboard.areaConsumptionChart.title`)"
  >
    <template #title-icon>
      <div class="bg-gray-100 rounded-full !w-[20px] !h-[20px] flex justify-center items-center">
        <i class="pi pi-bolt !text-xs text-text-secondary" />
      </div>
    </template>

    <template #header-right>
      <slot name="header-right" />
    </template>

    <template #infos>
      <i18n-t
        class="whitespace-pre-line"
        :keypath="keyOf(k.data.dashboard.areaConsumptionChart.tooltip[props.type])"
        tag="p"
      >
        <template #sitesManagementTab>
          <RouterLink
            target="_blank"
            :to="{
              name: isEnvFeatureEnabled(newSectionManagementFeatureFlag)
                ? ERouteName.SECTION_MANAGEMENT_NEW
                : ERouteName.SECTION_MANAGEMENT,
            }"
          >
            <HospitalityButton
              :label="sitesManagementTab"
              severity="link"
              size="xs"
            />
          </RouterLink>
        </template>
      </i18n-t>
    </template>

    <div
      v-if="!props.isLoading && !props.series?.length"
      class="w-full min-h-[25rem] flex justify-center items-center"
    >
      <span class="text-text-secondary">
        {{ t(k.global.noData) }}
      </span>
    </div>

    <BaseColumnChart
      v-else
      :column-chart-specs="sectionConsumptionCostChartSpecs"
      :has-no-data="props.hasNoData"
      :is-loading="props.isLoading"
      :navigable="true"
      :series="props.series"
    >
      <template #noDataMessage>
        <slot name="noDataMessage" />
      </template>
    </BaseColumnChart>
  </DashboardCard>
</template>
